import { Link } from 'react-router-dom';
import { formatDateTime } from '@/features/common/formatDate';
import { Icon } from '@/features/common/Icon';
import { domainLabel } from '@/features/common/useThematicDomains';
import { publicEvents } from '@/i18n/dictionaries/publicEvents';
import { useTranslation } from '@/i18n/useTranslation';
import { EVENT_KIND_LABEL } from '@/features/publicsite/events/eventKind';
import { eventTextLang } from '@/features/publicsite/events/eventLanguage';
import { EventMarkerBadge } from '@/features/publicsite/events/EventMarkerBadge';
import { eventIsOnMap } from '@/features/publicsite/events/eventOnMap';
import type { EventDetailHeaderProps } from '@/features/publicsite/types/events/EventDetailHeaderProps';

/**
 * The way back to the calendar, then the event's kind, title, date, organiser, place and raion,
 * and the themes it is filed under — the raion and the themes being what the calendar filters on,
 * which the page used to receive and never show.
 */
export function EventDetailHeader({ event }: EventDetailHeaderProps) {
  const { t, language } = useTranslation(publicEvents);
  const place = [event.location, event.regionName].filter(Boolean).join(', ');
  return (
    <>
      {/* The way back leads the page, as a breadcrumb rather than a link parked at the right of
        the heading. On the old layout it sat opposite the title, which put the one control that
        undoes the navigation furthest from where the eye starts. */}
      <Link className="detail-back" to="/evenimente">
        <Icon name="chevron-left" className="detail-back-icon" />
        {t('backToCalendar')}
      </Link>

      <span className="eyebrow">{t(EVENT_KIND_LABEL[event.kind])}</span>
      <h1 className="event-detail__title" lang={eventTextLang(event, 'title', language)}>
        {event.title}
      </h1>
      <p className="event-detail__meta">
        <span>
          <Icon name="calendar" className="event-detail__meta-icon" />
          {formatDateTime(event.startsAt, language)}
        </span>
        <EventMarkerBadge event={event} />
        {event.organizerName && (
          <span>
            <Icon name="people" className="event-detail__meta-icon" />
            {event.organizerName}
          </span>
        )}
        {/* The raion's name comes from the nomenclature, a Romanian proper noun in either language,
          so the one `lang` follows the location it is joined to. */}
        {place && (
          <span lang={eventTextLang(event, 'location', language)}>
            <Icon name="map-pin" className="event-detail__meta-icon" />
            {place}
          </span>
        )}
        {/* On the raion, not the location: an event is placed on the map by its raion
          (EventService.syncGeoPoint), so a location with no raion linked to an empty map. */}
        {eventIsOnMap(event) && (
          <Link className="event-detail__map-link" to={`/harta?subjectId=${event.id}`}>
            {t('seeOnMap')}
          </Link>
        )}
      </p>
      {event.themeLabels.length > 0 && (
        <p className="event-detail__themes">
          {t('themes')}: {event.themeLabels.map((theme) => domainLabel(theme, language)).join(', ')}
        </p>
      )}
    </>
  );
}
